import { passwordResetRequestSchema } from "@procuremate/shared";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { AuthLayout, FormError, FormNotice } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { api } from "@/lib/api-client";
import { authErrorMessage } from "./error-message";

/** The response is identical whether or not the email exists (no account enumeration). */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>();

  const request = useMutation({
    mutationFn: (body: { email: string }) => api<void>("/auth/password-reset/request", { body }),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = passwordResetRequestSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(t("auth.invalidEmail"));
      return;
    }
    setFieldError(undefined);
    request.mutate(parsed.data);
  }

  return (
    <AuthLayout title={t("auth.forgotTitle")} subtitle={t("auth.forgotSubtitle")}>
      {request.isSuccess ? (
        <FormNotice message={t("auth.resetSent")} />
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <TextField
            id="forgot-email"
            label={t("auth.email")}
            type="email"
            autoComplete="username"
            required
            value={email}
            error={fieldError}
            onChange={(event) => setEmail(event.target.value)}
          />
          <FormError message={authErrorMessage(request.error, t)} />
          <Button type="submit" className="mt-2 w-full" disabled={request.isPending}>
            {request.isPending ? t("auth.sending") : t("auth.sendResetLink")}
          </Button>
        </form>
      )}
      <Link to="/login" className="mt-4 block text-center text-sm text-link underline-offset-4 hover:underline">
        {t("auth.backToSignIn")}
      </Link>
    </AuthLayout>
  );
}
